import { getMapsetBySEO } from "~/util/mapset";
import { buildLookupOptions, lookupbox } from "../poiagg";
import Scene from "~/scene";
import WasmStoreMarker from "~/wasm/store/wasm-store-marker";
import { CONFIG } from "~/config";
import { ILonLatPosition } from "~/interfaces";
import { WASM_ZINDEX } from "~/wasm/const";
import { addListener } from "~/lib/signals";
import { store } from "~/state/store";
import WasmBulkMode from "~/wasm/store/bulk-mode";

interface IPoi {
	source: "foto" | "wcam";
	id: number;
	mark: ILonLatPosition;
	markCamera?: ILonLatPosition;
	markOrigin?: ILonLatPosition;
	size: Array<number>;
	title: string;
	url: string;
}

export default class FotoWcamLookupbox {
	_request: any;

	_mapset: number;

	_sources: Array<string>;

	_wasmMarkers: Array<WasmStoreMarker>;

	_wasmMapping: {[key: string]: WasmStoreMarker;};

	_isEnabled: boolean;

	_webcamMarker: WasmStoreMarker;

	constructor() {
		this._request = null;
		this._mapset = getMapsetBySEO("fotografie").id;
		this._sources = [];
		this._wasmMarkers = [];
		this._isEnabled = false;
		this._wasmMapping = {};
		this._webcamMarker = null;

		addListener(this, "detail-activated", "_detailActivated");
		addListener(this, "detail-deactivated", "_detailDeactivated");
	}

	setEnabled(enabled: boolean) {
		this._isEnabled = enabled;

		if (CONFIG.wasmEnabled) {
			// smazeme
			if (!this._isEnabled) {
				const wasmBulk = new WasmBulkMode();

				for (const marker of this._wasmMarkers) {
					wasmBulk.removeMarker(marker);
				}

				wasmBulk.run();

				this._wasmMarkers.length = 0;
				this._wasmMapping = {};
			}
		}
	}

	setSources({
		foto = false,
		webcams = false,
	} : {
		foto?: boolean;
		webcams?: boolean;
	}) {
		const newSources: Array<string> = [];

		if (foto) {
			newSources.push("foto");
		}

		if (webcams) {
			newSources.push("wcam");
		}

		this._sources = newSources;
	}

	lookupbox() {
		if (this._request) {
			this._request.abort();
			this._request = null;
		}

		const lookupOpts = buildLookupOptions(this._mapset, {
			approval: true,
			sources: this._sources,
		});
		const viewport = Scene.getViewport();
		const lookupboxData = lookupbox(viewport.lbx, viewport.lby, viewport.rtx, viewport.rty, lookupOpts);

		this._request = lookupboxData.request;

		if (CONFIG.wasmEnabled) {
			lookupboxData.promise.then(data => {
				// @ts-ignore
				this._wasmLookup(data);
			});
		}

		return lookupboxData.promise;
	}

	_wasmLookup(data: { poi: Array<IPoi>; }) {
		const curIds = Object.keys(this._wasmMapping);
		const newMarkers: Array<WasmStoreMarker> = [];

		if (!this._isEnabled) {
			return;
		}

		const wasmBulk = new WasmBulkMode();

		for (const poi of data.poi) {
			const id = `${poi.source}_${poi.id}`;
			const ind = curIds.indexOf(id);

			if (ind !== -1) {
				curIds.splice(ind, 1);
			}

			if (id in this._wasmMapping) {
				newMarkers.push(this._wasmMapping[id]);
			} else {
				let marker: WasmStoreMarker = null;
				// lepsi kvalita
				const url = poi.url.replace("{width}", "80").replace("{height}", "80");

				if (poi.source === "foto") {
					marker = wasmBulk.createMarker({
						type: "photo",
						visual: "inactive",
						lon: poi.mark.lon,
						lat: poi.mark.lat,
						zIndex: WASM_ZINDEX.marker.photo,
						interactionType: "click-hover",
						customData: {
							url,
							poi: {
								source: poi.source,
								id: poi.id,
							},
						},
					});
				} else if (poi.source === "wcam") {
					marker = wasmBulk.createMarker({
						type: "webcam",
						visual: "inactive",
						lon: poi.mark.lon,
						lat: poi.mark.lat,
						zIndex: WASM_ZINDEX.marker.webcam,
						interactionType: "click-hover",
						customData: {
							url,
							poi: {
								source: poi.source,
								id: poi.id,
							},
						},
					});
				}

				if (marker) {
					this._wasmMarkers.push(marker);
					this._wasmMapping[id] = marker;
					newMarkers.push(marker);
				}
			}
		}

		for (const oldId of curIds) {
			wasmBulk.removeMarker(this._wasmMapping[oldId]);
			delete this._wasmMapping[oldId];
		}

		this._wasmMarkers = newMarkers;

		wasmBulk.run();
	}

	_detailActivated() {
		this._detailDeactivated();

		const data = store.getState().detail.data;

		for (const wasmMarker of this._wasmMarkers) {
			const markerData = wasmMarker.data.customData?.poi;

			if (markerData && markerData.id === data.id && markerData.source === data.source) {
				this._webcamMarker = wasmMarker;
				this._webcamMarker.update({
					visual: "standard",
				});
			}
		}
	}

	_detailDeactivated() {
		if (this._webcamMarker) {
			this._webcamMarker.update({
				visual: "inactive",
			});
			this._webcamMarker = null;
		}
	}
}
